import { SliderTypes } from './../../../partials/ui/rating-slider/rating-slider.component';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ElementModel } from '../../../models/element.model';
import { DataService } from '../../../services/data.service';
import { ActivatedRoute } from '@angular/router';
import { AcModel } from '../../../models/ac.model';
import { CompetenceModel } from 'projects/core/src/lib/models/competence.model';
import { RatingtypeModel } from '../../../models/ratingtype.model';
import { BehavioranchorModel } from 'projects/core/src/lib/models/behavioranchor.model';
import { ApiService } from '../../../services/api.service';
import { AuthStateService } from '../../../services/auth-state.service';
import {
  faTrafficLightStop,
  faBan
} from '@fortawesome/pro-light-svg-icons';
import { ObserverModel } from 'projects/core/src/lib/models/observer.model';
import { Subscription, of, timer } from 'rxjs';
import { catchError, switchMap, takeWhile } from 'rxjs/operators';
import { SlotModel } from '../../../models/slot.model';
import { TranslocoService } from '@ngneat/transloco';
import { UiService } from '../../../services/ui.service';
import { AppService } from '../../../services/app.service';
import { CandidateModel } from '../../../models/candidate.model';
import { ExerciseModel } from '../../../models/exercise.model';
import { GroupModel } from '../../../models/group.model';

const POLLING_INTERVAL = 5;
const START_VALUE = -1;

@Component({
  selector: 'app-rating',
  templateUrl: './rating.component.html',
  styleUrls: ['./rating.component.scss']
})
export class RatingComponent implements OnInit, OnDestroy {

  private polling: Subscription = null;

  public ac: AcModel = null;
  public slot: SlotModel = null;
  public exercise: ExerciseModel = null;
  public element: ElementModel = null;
  public candidate: CandidateModel = null;
  public competences: Array<CompetenceModel> = [];
  public ratingtype: RatingtypeModel = null;
  public myRatings: any = {};
  public foreignRatings: any = null;
  public language: string = null;
  private slotId: number = null;
  private groupHash: string = null;
  private candidateHash: string = null;
  private ratingSubscription: Subscription = null;
  private acSubscription: Subscription = null;
  private networkErrorSubscription: Subscription = null;
  private networkError = false;
  public isMod = false;
  public comments: string | null = null;

  public group: GroupModel = null;
  public slidersActive = false;

  public faTrafficLightStop = faTrafficLightStop;
  public faBan = faBan;

  public SliderTypes = SliderTypes;

  constructor(
    private route: ActivatedRoute,
    private app: AppService,
    private data: DataService,
    private api: ApiService,
    private ui: UiService,
    public auth: AuthStateService,
    public transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.ui.setPageTitle(this.transloco.translate('common.labels.rating'));
    this.language = this.transloco.getActiveLang();
    this.acSubscription = this.data.ac$.subscribe({
      next: (ac: AcModel) => {
        if (ac !== null) {
          this.ac = ac;
          this.ratingtype = this.ac.ratingtype;
          this.setupData();
        }
      }
    });
    this.networkErrorSubscription = this.data.networkError$.subscribe((networkError: boolean) => {
      this.networkError = networkError;
    });
    this.route.params.subscribe(params => {
      this.slotId = parseInt(params.slot, 10) || null;
      this.groupHash = params.group || null;
      this.candidateHash = params.candidate || null;
      this.setupData();
    });
    this.transloco.langChanges$.subscribe(() => {
      this.language = this.transloco.getActiveLang();
    });
    // subscribe to ratings
    this.ratingSubscription = this.api.rating$.subscribe({
      next: (result: any) => {
        const ratings = [];
        if (result.data && result.data.length > 0) {
          result.data.forEach((rating: any) => {
            ratings.push(rating);
          });
          this.ac.ratings = ratings;
        }
      },
      error: () => {
        this.ui.toastError(this.transloco.translate('rating.messages.rating_not_saved'));
      }
    });
  }

  ngOnDestroy(): void {
    if (this.slot !== null) {
      this.slot.messageBeforeFinish = null;
    }
    if (this.polling !== null) {
      this.polling.unsubscribe();
      this.polling = null;
    }
    if (this.acSubscription !== null) {
      this.acSubscription.unsubscribe();
      this.acSubscription = null;
    }
    if (this.ratingSubscription !== null) {
      this.ratingSubscription.unsubscribe();
      this.ratingSubscription = null;
    }
    if (this.networkErrorSubscription !== null) {
      this.networkErrorSubscription.unsubscribe();
      this.networkErrorSubscription = null;
    }
  }

  public isActive(): boolean {
    if (this.networkError === true) {
      return false;
    }
    const hash = this.data.getHash(this.slot, this.candidate, this.group);
    this.slidersActive = this.slot.isRatingActive(hash) && !this.auth.isSilentObserver();
    return this.slidersActive;
  }

  public changeCandidate(candidate: CandidateModel = null): void {
    this.candidate = candidate || null;
    this.loadMyCandidateRatings();
  }

  public calcCompetence(competence: CompetenceModel): void {
    let sum = 0;
    let count = 0;
    competence.behavioranchors.forEach((behavioranchor: BehavioranchorModel) => {
      if (behavioranchor.value >= 0) {
        sum += behavioranchor.value;
        count++;
      }
    });
    competence.value = (count > 0) ? Math.round(sum / count * (1 / this.ratingtype.step)) * this.ratingtype.step : -1;
  }

  public saveRating(): void {
    const comments = (this.auth.isModerator()) ? this.comments : null;
    this.candidate.setRatingComment(this.slot.id, comments);
    this.setMessageBeforeFinish();
    this.api.setRating('exercise', this.slot.id, this.candidate?.hashed_id, this.getPayload(), comments);
  }

  public getForeignValue(hash: string, competence: CompetenceModel): string {
    if (
      this.candidate &&
      this.foreignRatings[this.candidate.hashed_id] &&
      this.foreignRatings[this.candidate.hashed_id][hash] &&
      this.foreignRatings[this.candidate.hashed_id][hash][competence.id]
    ) {
      const value = this.foreignRatings[this.candidate.hashed_id][hash][competence.id].value;
      if (value >= 0) {
        return (this.auth.isModerator()) ? value : '<i class="pi pi-check">';
      } else {
        return '-';
      }
    }
    return '';
  }

  private getPayload(): any {
    const payload = {};
    this.competences.forEach((competence: CompetenceModel) => {
      const behavioranchors = {};
      competence.behavioranchors.forEach((behavioranchor: BehavioranchorModel) => {
        behavioranchors[behavioranchor.id] = behavioranchor.value;
      });
      payload[competence.id] = {
        value: competence.value,
        behavioranchors
      };
    });
    return payload;
  }

  private setupData(): void {
    if (this.ac !== null && this.slotId !== null) {
      const slot = this.ac.getSlotOnId(this.slotId);
      if (slot !== null) {
        this.slot = slot;
        this.data.setActiveExercise(this.slot.id);
        this.exercise = this.ac?.getExercise(this.slot.exercise_id);
        if (this.groupHash !== null) {
          this.group = this.exercise.groups.find(group => group.hash === this.groupHash) || null;
        }
        if (this.candidateHash !== null) {
          this.candidate = this.ac?.candidates.find(candidate => candidate.hashed_id === this.candidateHash) || null;
        }

        this.isMod = this.auth.isModerator() || this.group?.isGroupModerator(this.auth.getHashedId());
        this.initCompetences();
        this.initPolling();
        this.changeCandidate(this.candidate);
      } else {
        this.app.redirectToDashboard();
      }
    }
  }

  private loadMyCandidateRatings(): void {
    this.myRatings = {};
    // filter ratings for slot from ac
    this.ac.ratings.filter((rating: any) => {
      return rating.exercisepart === this.slot.exercisepart_id &&
        rating.candidate === this.candidate?.id;
    }).forEach((rating: any) => {
      this.myRatings[rating.competence] = {
        value: rating.value,
        behavioranchors: rating.behavioranchors
      };
    });
    // load ratings to competences
    this.competences.forEach((competence: CompetenceModel) => {
      competence.value = (this.myRatings[competence.id] !== undefined) ? this.myRatings[competence.id].value : START_VALUE;
      competence.behavioranchors.forEach((behavioranchor: BehavioranchorModel) => {
        behavioranchor.value =
          this.myRatings[competence.id] !== undefined && this.myRatings[competence.id].behavioranchors[behavioranchor.id] !== undefined
            ? this.myRatings[competence.id].behavioranchors[behavioranchor.id]
            : START_VALUE;
      });
    });
    // set comments
    if (this.isMod === true) {
      this.comments = this.candidate?.getRatingComment(this.slot?.id) || null;
      this.setMessageBeforeFinish();
    }
  }

  private setMessageBeforeFinish(): void {
    if (this.ac !== null && this.slot !== null) {
      const message = (this.ac.isGroup()) ? 'rating.messages.rating_comments_empty_group' : 'rating.messages.rating_comments_empty_single';
      const candidates = (this.ac.isGroup() && this.group !== null)
        ? this.group.candidates
        : this.ac.candidates;
      let missesComments = false;
      candidates.forEach((candidate: CandidateModel) => {
        const comment = candidate.getRatingComment(this.slot.id);
        if (comment === null || comment === '') {
          missesComments = true;
        }
      });
      this.slot.messageBeforeFinish = (missesComments) ? this.transloco.translate(message) : null;
    }
  }

  private initCompetences(): void {
    this.competences = [...this.ac?.competences].filter((competence: CompetenceModel) => {
      const behavioranchors = this.slot?.behavioranchors?.filter((behavioranchor: BehavioranchorModel) => {
        return behavioranchor.competence_id === competence.id;
      }) || [];
      if (behavioranchors.length > 0) {
        competence.behavioranchors = behavioranchors;
        return true;
      } else {
        return false;
      }
    });
    this.competences.sort((a: any, b: any) => (
      a.position > b.position) ? 1 : ((b.position > a.position) ? -1 : 0)
    );
  }

  private initForeignRatings(): void {
    if (Array.isArray(this.ac?.candidates) && Array.isArray(this.ac?.observers)) {
      this.foreignRatings = {};
      this.ac.candidates.forEach((candidate: CandidateModel) => {
        this.foreignRatings[candidate.hashed_id] = {};
        // observers
        this.ac.observers.forEach((observer: ObserverModel) => {
          const competenceData = {};
          this.competences.forEach((competence: CompetenceModel) => {
            competenceData[competence.id] = {
              value: START_VALUE,
              behavioranchors: null
            };
          });
          this.foreignRatings[candidate.hashed_id][observer.hashed_id] = competenceData;
        });
        // moderator
        if (this.isMod) {
          const competenceData = {};
          this.competences.forEach((competence: CompetenceModel) => {
            competenceData[competence.id] = {
              value: START_VALUE,
              behavioranchors: null
            };
          });
          this.foreignRatings[candidate.hashed_id][this.ac.moderator?.hashed_id] = competenceData;
        }
      });
    }
  }

  private initPolling(): void {
    if (this.polling === null) {
      this.initForeignRatings();
      this.polling = timer(0, POLLING_INTERVAL * 1000)
        .pipe(
          takeWhile(() => this.isMod === true),
          switchMap(() => {
            return this.api.getRatings(this.slot.id)
              .pipe(
                catchError(() => {
                  return of(null);
                })
              );
          })
        ).subscribe((result: any) => {
          if (result !== null) {
            result.data?.forEach((rating: any) => {
              if (
                this.foreignRatings[rating.candidate] !== undefined &&
                this.foreignRatings[rating.candidate][rating.rater] !== undefined &&
                this.foreignRatings[rating.candidate][rating.rater][rating.competence] !== undefined
              ) {
                this.foreignRatings[rating.candidate][rating.rater][rating.competence] = {
                  value: rating.value,
                  behavioranchors: rating.behavioranchors
                };
              }
            });
          }
        });
    }
  }

}
